import { Formik, Field, Form, ErrorMessage, useFormikContext, isSubmitting } from 'formik';
import { useNavigate, useLocation, useParams } from "react-router-dom";
import * as Yup from 'yup';
import { useState, useEffect, useRef, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import {
  DatePicker, 
  TimePicker, 
  ColorPicker, 
  CreateText, 
  ProductCard, 
  titleCaseText, 
  SearchPreview, 
  PicksDisplay, 
  WarningIcon, 
  Loading,
  EditMenu,
  DropDownFilter,
  TablePagination,
  ToolTip
} from './utility/Utility';
import { Scorecard } from './utility/Charts';
import axios from 'axios';
import { ActionDescription } from './Onboarding';

export const CustomerSummary = (props) => {
  const {customer, setCustomer, tutorial, setTutorial, logout} = props;
  const { getJwtToken, canWrite } = useContext(AuthContext);
  const [adjusting, setAdjusting] = useState(false);
  const dollar = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD"
  })
  return (
    <>
      {adjusting && <CustomerAdjustmentForm 
        customer={customer} 
        setCustomer={setCustomer}
        setAdjusting={setAdjusting}
        tutorial={tutorial}
        setTutorial={setTutorial}
        logout={logout}
        getJwtToken={getJwtToken}
      />}
      <div className="customer-summary centralize" style={{position: "relative"}}>
        {!customer && <Loading className="customer-summary__loading" />}
        {customer && 
          <>
            <div className="customer-summary__email">{customer.email}</div>
            <div className="customer-summary__stats" style={tutorial === 1 ? {
              position: "relative", 
              zIndex: "55", 
              backgroundColor: "#fff",
              boxShadow: "0 0 10px #fff",
              width: "fit-content",
              margin: "auto",
              paddingTop: "25px",
              borderRadius: "var(--large-border-radius)"
            } : {}}>
              <div className="customer-summary__stats-row">
                <Scorecard className="customer-summary__stat" data={customer.points ? customer.points : "0"} title="Points" />
              </div>
              <div className="customer-summary__stats-row">
                <Scorecard className="customer-summary__stat" data={customer.orders.length} title="Orders"/>
                <Scorecard className="customer-summary__stat" data={dollar.format(customer.orders.reduce((acc, v) => acc + v.subtotal, 0))} title="Total Spent"/>
                <Scorecard className="customer-summary__stat" data={dollar.format(customer.orders.length > 0 ? customer.orders.reduce((acc, v) => acc + v.subtotal, 0) / customer.orders.length : 0)} title="AOV"/>
              </div>
            </div>
            {tutorial === 1 && 
              <ActionDescription 
                text={`View important metrics about your customer.`}
                style={{width: "220px", left: "calc(50% - 120px)", bottom:"-50px", textAlign: "left"}}
                triangle={{top: "-18px", left: "calc(50% - 9px)"}}
                tutorial={tutorial}
                setTutorial={setTutorial}
                next="Next"
              />
            }
            <div className="customer-summary__button-container" style={{position: "relative"}}>
              <button 
                disabled={!canWrite}
                onClick={() => {
                  if (tutorial === 3) setTutorial(4);
                  setAdjusting(!adjusting);
                }} 
                className="customer-summary__make-adjustment btn gradient"
                style={tutorial === 3 ? {
                  position: "relative", 
                  zIndex: "55", 
                  backgroundColor: "#fff",
                  boxShadow: "0 0 10px #fff",
                } : {}}
              >Make Adjustment</button>
              {tutorial === 3 && 
                <ActionDescription 
                  text={`Click to make a point adjustment to your customer.`}
                  style={{width: "220px", right: "220px", bottom:"-8px", textAlign: "left"}}
                  triangle={{right: "-18px", top: "calc(50% - 9px)", transform: "rotate(90deg)"}}
                  tutorial={tutorial}
                  setTutorial={setTutorial}
                />
              }
            </div>
          </>
        }
      </div>
    </>
  )
}

export const CustomerTable = (props) => {
  const {customer} = props;
  return (
    <div className="customer-table">
      {!customer && <Loading className="customer-summary__loading" />}
      {customer && 
        <></>
      }
    </div>
  )
}

const CustomerAdjustmentForm = (props) => {
  const {customer, setCustomer, setAdjusting, tutorial, setTutorial, logout, getJwtToken} = props;
  const [values, setValues] = useState({points: '', reason: ''});
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  
  useEffect(() => {
    if (tutorial > 0) setValues({points: '50', reason: "Defective Product"})
  }, [])

  const updateValues = (n, v) => {
    setValues({...values, [n]: v});
  }

  const submit = async () => {
    if (tutorial === 0) {
      try {
        const token = await getJwtToken();
        if (!token) {
          logout();
          return;
        }
        
        const {points, reason} = values;
        if (!points) {
          setError('Points must be a positive or negative whole number.');
          return;
        }

        const disabled = [
          ...document.querySelectorAll('.customer-adjustment-form__input'),
          ...document.querySelectorAll('.customer-adjustment-form__buttons button')
        ];
        disabled.forEach(t => t.disabled = true);
        
        await axios.put('https://api.amplifyloyalty.com/app/customers', {
          _id: customer._id,
          points,
          reason
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        navigate(0);

      } catch (err) {
        console.log(err);
        // Re-enable form on error
        const disabled = [
          ...document.querySelectorAll('.customer-adjustment-form__input'),
          ...document.querySelectorAll('.customer-adjustment-form__buttons button')
        ];
        disabled.forEach(t => t.disabled = false);
        // Potentially set an error message to display to the user
        setError('An error occurred. Please try again.');
      }
    } else {
      setCustomer({...customer,
        events: [
          {
            name: "Defective Product",
            points: 50,
            time: new Date()
          },
          ...customer.events
        ],
        points: 550 
      })
      setTutorial(8);
      setAdjusting(false);
    } 
  }

  return (
    <div className="flex-popup-overlay">
      {tutorial > 3 && tutorial < 8 && <div className="popup-overlay" style={{backgroundColor: "rgb(0, 0, 0, 0)"}}></div> }
      <div className="customer-adjustment-form" style={{position: "relative"}}>
        {tutorial === 4 && 
          <ActionDescription 
            text={`Input the number of points you would like to add. Input a negative value to subtract.`}
            style={{width: "220px", right: "-260px", top:"15px", textAlign: "left"}}
            triangle={{left: "-18px", top: "calc(50% - 9px)", transform: "rotate(-90deg)"}}
            tutorial={tutorial}
            setTutorial={setTutorial}
            next="Next"
            hidePopup={true}
          />
        }
        {tutorial === 5 && 
          <ActionDescription 
            text={`Input a reason for the adjustment. Your customers will see this when they login to their account.`}
            style={{width: "200px", right: "-240px", top:"42px", textAlign: "left"}}
            triangle={{left: "-18px", top: "calc(50% - 9px)", transform: "rotate(-90deg)"}}
            tutorial={tutorial}
            setTutorial={setTutorial}
            next="Next"
            hidePopup={true}
          />
        }
        {tutorial === 6 && 
          <ActionDescription 
            text={`The new point total will be displayed here.`}
            style={{width: "170px", right: "-210px", bottom:"32px", textAlign: "left"}}
            triangle={{left: "-18px", top: "calc(50% - 9px)", transform: "rotate(-90deg)"}}
            tutorial={tutorial}
            setTutorial={setTutorial}
            next="Next"
            hidePopup={true}
          />
        }
        <div className="customer-adjustment-form__header">Adjust by</div>
        <div className="customer-adjustment-form__input-container">
          <label htmlFor="points">Points</label>
          <input 
            className="customer-adjustment-form__input" 
            type="number" 
            name="points" 
            onChange={(e) => updateValues('points', e.target.value)}
            value={values.points}
          />
        </div>
        <div className="customer-adjustment-form__input-container">
          <label htmlFor="reason">Reason</label>
          <ToolTip message="This is customer facing and will be seen in the customer's profile." width="150px" />
          <input 
            className="customer-adjustment-form__input" 
            type="text" 
            name="reason" 
            onChange={(e) => updateValues('reason', e.target.value)}
            value={values.reason}
          />
        </div>
        {error && <div className="customer-adjustment-form__errors">{error}</div>}
        <div className="customer-adjustment-form__point-total">
          New total: <span className="customer-adjustment-form__points gradient-text">{customer.points + (values.points ? parseInt(values.points) : 0)}</span>
        </div>
        <div className="customer-adjustment-form__buttons" style={{position: "relative"}}>
          <button onClick={() => setAdjusting(false)} className="customer-adjustment-form__cancel btn">Cancel</button>
          <button className="btn gradient" onClick={submit} style={tutorial === 7 ? {position: "relative", zIndex: "55"} : {}}>Submit</button>
          {tutorial === 7 && 
            <ActionDescription 
              text={`Click the Submit button to make the adjustment.`}
              style={{width: "170px", right: "-230px", bottom:"-15px", textAlign: "left"}}
              triangle={{left: "-18px", top: "calc(50% - 9px)", transform: "rotate(-90deg)"}}
              tutorial={tutorial}
              setTutorial={setTutorial}
              hidePopup={true}
            />
          }
        </div>
      </div>
    </div>
  )
}